import { roundMoney } from '../../utils/money';
import { isCustomerPickup } from './transportDirection';
import {
  buildFreightLine,
  buildFreightTotals,
  positiveQuantity,
  type FreightLineInput,
  type FreightLineRow,
  type FreightLineUnitRow,
  type FreightLinesTotals,
} from './freightGoodsTable';
import type { RouteOrderDto, TransportCargoItemDto } from '../../types/transport';

/**
 * OUTBOUND truck (or a legacy mixed one until it is saved again): what each
 * customer order (ZK) loads on this truck and the part of the truck's cost that
 * falls on it. Read-only presentation of backend facts — the split between ZK
 * is owned by `transportService.syncTransportCostsInTransaction`
 * (`TransportOrderAllocation.allocatedTransportCost`, canonical PLN) and, per
 * line, by `utils/customerOrderLineFreight.js` (`cargoItems[].freightPLN`,
 * adds up to the ZK share). Quantities are the ZK's cargo lines on the truck
 * (planned pcs/m²/m³). Rows, totals and averages come from the shared
 * `freightGoodsTable.ts`; nothing is re-split here.
 *
 * Only orders with a positive share get a card — a ZK riding an INBOUND truck
 * keeps a 0 % allocation and pays through the WZ batch cost instead (WP14).
 * A unit a line has no stored equivalent for (a window has no m³) is not
 * offered, and a total in a unit only exists when every line has it.
 */
export type CustomerOrderLineUnitRow = FreightLineUnitRow;
export type CustomerOrderLineRow = FreightLineRow;

export interface CustomerOrderFreightRow {
  id: string;
  label: string;
  customerName: string | null;
  customerPickup: boolean;
  share: {
    percent: number | null;
    amountPLN: number;
  };
  lines: CustomerOrderLineRow[];
  /** "Razem / Średnio" of the goods table; `null` when the ZK has no cargo lines on this truck. */
  totals: FreightLinesTotals | null;
}

export interface CustomerOrderFreightSummary {
  rows: CustomerOrderFreightRow[];
}

function toFreightLine(item: TransportCargoItemDto): FreightLineInput {
  return {
    id: item.id,
    name: item.productName,
    width: item.width,
    height: item.height,
    length: item.length,
    thickness: item.thickness,
    dimensionDisplayProfile: item.dimensionDisplayProfile,
    quantityPcs: item.quantityPcs,
    quantityM2: item.quantityM2,
    quantityM3: item.quantityM3,
    freightPLN: item.freightPLN,
  };
}

export function summarizeCustomerOrderFreight(
  routeOrders: RouteOrderDto[] | undefined,
  cargoItems: TransportCargoItemDto[] | undefined,
): CustomerOrderFreightSummary {
  const cargo = cargoItems || [];
  const rows = (routeOrders || []).flatMap((routeOrder): CustomerOrderFreightRow[] => {
    // Undefined without transport-cost access; 0 on an INBOUND truck.
    const amountPLN = positiveQuantity(routeOrder.allocatedTransportCost);
    if (amountPLN === null) return [];
    const costPLN = roundMoney(amountPLN);
    const items = cargo.filter((item) => item.orderId === routeOrder.orderId).map(toFreightLine);
    const order = routeOrder.order;
    return [{
      id: routeOrder.orderId,
      label: order?.displayOrderNumber || order?.orderNumber || routeOrder.orderId,
      customerName: order?.customer?.name || order?.customerName || null,
      customerPickup: isCustomerPickup(order),
      share: {
        percent: routeOrder.allocationPercent ?? null,
        amountPLN: costPLN,
      },
      lines: items.map(buildFreightLine),
      totals: buildFreightTotals(items, costPLN),
    }];
  });
  return { rows };
}
